import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Badge,
  Grid,
  GridItem,
  useToast,
  Button,
  useDisclosure,
  Icon,
  Tooltip,
  Input,
  IconButton,
} from "@chakra-ui/react";
import { CheckIcon, EditIcon, CloseIcon } from "@chakra-ui/icons";
import { FaMagic, FaAmazon } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import Card from "../components/Card/Card";
import CardBody from "../components/Card/CardBody";
import CardHeader from "../components/Card/CardHeader";
import { useAuth } from "../context/AuthContext";
import { pickContextFields } from "../services/contextService";
import { useProject } from "../context/ProjectContext";
import KeywordsCreator from "./KeywordsCreator";
import BulletCreator from "./BulletCreator";
import MarketplaceSelector from "./MarketplaceSelector";
import ProductBriefCreator from "./ProductBriefCreator";
import DescriptionWizard from "./DescriptionWizard/DescriptionWizard";
import ProjectSidebarImages from "./ProjectSidebarImages";
import ProductImageCarousel, { buildProductImagesList } from "./ProductImageCarousel";
import ChatWithAbu from "./Chat/ChatWithAbu";
import LoadingOverlay from "../components/LoadingOverlay";

const ViewProject: React.FC<{ 
setProjectName: (name: string) => void }> = ({
  setProjectName,
}) => {
  const { projectId } = useParams<{ projectId: string | undefined }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, getToken } = useAuth();
  const { project, setProject, description, setDescription, productBrief, setProductBrief } = useProject();
  const toast = useToast();
  const { t } = useTranslation();
  
  // États de chargement
  const [loading, setLoading] = useState<boolean>(false); // Initialisé à false, sera mis à true lors du fetch
  const [initialLoadDone, setInitialLoadDone] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // États du wizard
  const [wizardSkipped, setWizardSkipped] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  
  // États d'upload d'images
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingImage, setDeletingImage] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | undefined>(undefined);
  
  // États du chat
  const { isOpen: isChatOpen, onOpen: onChatOpen, onClose: onChatClose } = useDisclosure();
  const [chatPendingMessage, setChatPendingMessage] = useState<{ content: string, agentId?: string } | null>(null);
  
  // États de réimport Amazon
  const [isReimporting, setIsReimporting] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // États d'édition inline
  const [isEditingBrand, setIsEditingBrand] = useState(false);
  const [editedBrand, setEditedBrand] = useState("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");

  // Index de l'image affichée dans le carousel (une seule image à l'écran = moins de lag)
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Récupérer le token d'auth pour les composants enfants
  useEffect(() => {
    if (currentUser) {
      currentUser.getIdToken().then(setAuthToken);
    }
  }, [currentUser]);
  
  // Cleanup du polling au démontage
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleSaveBrand = useCallback(async () => {
    if (!editedBrand.trim() || !project) return;
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/editProject", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ projectId, brand: editedBrand })
      });
      
      if (!response.ok) throw new Error("Failed to update brand");
      
      setProject({
        ...project,
        brand: editedBrand,
        context: { ...project.context, brand: editedBrand }
      });
      setIsEditingBrand(false);
      toast({ title: t("brandUpdated"), status: "success" });
    } catch (e) {
      toast({ title: t("error"), status: "error" });
    }
  }, [editedBrand, project, projectId, getToken, setProject, toast, t]);

  const handleSaveTitle = useCallback(async () => {
    if (!editedTitle.trim() || !project) return;
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/editProject", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ projectId, newName: editedTitle })
      });
      
      if (!response.ok) throw new Error("Failed to update title");
      
      setProject({
        ...project,
        name: editedTitle,
        data: { ...project.data, title: editedTitle }
      });
      setProjectName(editedTitle);
      setIsEditingTitle(false);
      toast({ title: t("titleUpdated"), status: "success" });
    } catch (e) {
      toast({ title: t("error"), status: "error" });
    }
  }, [editedTitle, project, projectId, getToken, setProject, setProjectName, toast, t]);

  const handleDeleteImage = async (imageUrl: string) => {
    try {
        setDeletingImage(imageUrl);
        const token = await getToken();
        // Extract filename from URL
        let filename = imageUrl.split('/').pop();
        if (filename?.includes('?')) {
            filename = filename.split('?')[0];
        }

        if (!filename) return;

        const response = await fetch("/.netlify/functions/deleteImage", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
                projectId,
                filename
            })
        });

        if (!response.ok) {
            throw new Error("Failed to delete image");
        }

        // Update local state
        if (project) {
            const currentImages = project.images || project.data?.images || [];
            const updatedImages = currentImages.filter((img: string) => img !== imageUrl);
            
            setProject({
                ...project,
                images: updatedImages
            });
            
            toast({
                title: t("imageDeleted"),
                status: "success",
                duration: 2000,
            });
        }

    } catch (error) {
        console.error("Delete error:", error);
        toast({
            title: t("deleteErrorTitle"),
            status: "error",
            duration: 3000,
        });
    } finally {
        setDeletingImage(null);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const toastId = toast({
      title: t("optimizingAndUploading"),
      status: "loading",
      duration: null,
      isClosable: false,
    });

    try {
      const token = await getToken();
      const newImages: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Sanitize filename to avoid key format issues
        const sanitizedFilename = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');

        // Upload via Presigned URL (Optimized)
        // 1. Get Presigned URL from Netlify (Proxy to Backend)
        const presignResponse = await fetch("/.netlify/functions/getPresignedUrl", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json", 
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({
            projectId, 
            filename: sanitizedFilename,
            contentType: file.type || "image/jpeg"
          }),
        });

        if (!presignResponse.ok) {
            throw new Error("Failed to get upload URL");
        }

        const { uploadUrl, filename: uniqueFilename, key } = await presignResponse.json();
        console.log("Presign response:", { uploadUrl, uniqueFilename, key });

        // 2. Upload directly to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          body: file, // Send raw file
          headers: {
            "Content-Type": file.type || "image/jpeg"
          }
        });

        if (!uploadResponse.ok) {
            throw new Error("Failed to upload to storage");
        }

        // 3. Confirm upload and get CloudFront signed URL
        const saveRefResponse = await fetch("/.netlify/functions/saveImageReference", {
            method: "POST",
            headers: { 
                "Content-Type": "application/json", 
                Authorization: `Bearer ${token}` 
            },
            body: JSON.stringify({
                projectId, 
                key: key // Pass the complete S3 key
            }),
        });
        
        if (!saveRefResponse.ok) {
            throw new Error("Failed to confirm upload");
        }
        
        const { url: cloudFrontUrl } = await saveRefResponse.json();
        newImages.push(cloudFrontUrl);
      }

      // Update local project state with new images
      if (newImages.length > 0 && project) {
          const currentImages = project.images || project.data?.images || [];
          const updatedProject = {
              ...project,
              images: [...currentImages, ...newImages]
          };
          setProject(updatedProject);
          
          toast.update(toastId, {
              title: t("imagesAddedSuccess"),
              status: "success",
              duration: 3000,
              isClosable: true,
          });
      }

    } catch (error) {
      console.error("Upload error:", error);
      toast.update(toastId, {
        title: t("uploadErrorTitle"),
        description: t("uploadErrorDesc"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
          fileInputRef.current.value = "";
      }
    }
  };


  // Fetch du projet - mémorisé avec useCallback pour stabilité
  const fetchProject = useCallback(async () => {
    if (!projectId || !currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await fetch("/.netlify/functions/getProject", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: currentUser.uid,
          projectId,
        }),
      });

      const result = await response.json();

      if (response.status !== 200) {
        setError(result.error || "Failed to load project");
      } else if (result.project) {
        setProject(result.project);
        setProjectName(result.project.name || "");
        const { description: desc, productBrief: pb } = pickContextFields(result.project);
        setDescription(desc);
        setProductBrief(pb ?? result.project?.productBrief ?? null);
      }
    } catch (err) {
      console.error("Fetch project error:", err);
      setError("Failed to load project data");
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [projectId, currentUser, getToken, setProject, setProjectName, setDescription, setProductBrief]);

  // Effect pour charger le projet au montage ou changement d'ID
  useEffect(() => {
    // Si le projet en contexte correspond déjà, pas besoin de recharger
    if (project && project.id === projectId) {
      setInitialLoadDone(true);
      return;
    }
    
    // Sinon, on charge
    fetchProject();
  }, [projectId, project?.id, fetchProject]);

  // Auto-lancer le réimport Amazon quand on arrive depuis un import inventaire
  const reimportTriggeredForRef = useRef<string | null>(null);
  useEffect(() => {
    const fromImport = (location.state as { fromAmazonImport?: boolean })?.fromAmazonImport;
    if (!fromImport || !projectId || !project || !initialLoadDone) return;
    if (reimportTriggeredForRef.current === projectId) return;
    const asin = project?.asin || project?.data?.asin;
    if (!asin) return;
    reimportTriggeredForRef.current = projectId;
    navigate(location.pathname, { replace: true, state: {} });
    handleReimport();
  }, [projectId, project, initialLoadDone, location.state]);

  const handleAnalyzeBullet = (text: string, index: number, agent: 'abu' | 'menga') => {
      const message = t('analyzeBulletPrompt', { index: index + 1, text });
      setChatPendingMessage({ content: message, agentId: agent });
      onChatOpen();
  };

  const handleReimport = async () => {
    const asin = project?.asin || project?.data?.asin;
    const marketplace = project?.marketplace || project?.tld || project?.data?.marketplace || 'fr';

    if (!project || !asin) {
        toast({
            title: t("asinMissing"),
            status: "warning",
            duration: 3000,
        });
        return;
    }

    setIsReimporting(true);
    try {
        const token = await getToken();
        if (!token) throw new Error("Authentication token missing");

        // 1. Trigger Scrape
        const triggerResponse = await fetch("/.netlify/functions/triggerAmazonScrape", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
                projectId,
                asin,
                marketplace,
                mode: "refresh"
            }),
        });

        if (!triggerResponse.ok) {
            const errorData = await triggerResponse.json();
            throw new Error(errorData.error || "Failed to trigger scrape");
        }

        const { jobId } = await triggerResponse.json();
        
        toast({
            title: t("reimportStarted"),
            description: t("reimportStartedDesc"),
            status: "info",
            duration: 2000,
        });

        // 2. Poll for Status (stocké dans ref pour cleanup)
        pollIntervalRef.current = setInterval(async () => {
            try {
                const statusResponse = await fetch("/.netlify/functions/checkAmazonJob", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify({ jobId }),
                });

                const statusData = await statusResponse.json();

                if (statusData.status === "completed") {
                    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
                    
                    // 3. Refresh Project Data
                    const refreshResponse = await fetch("/.netlify/functions/getProject", {
                        method: "POST",
                        headers: { 
                            "Content-Type": "application/json", 
                            "Authorization": `Bearer ${token}` 
                        },
                        body: JSON.stringify({ userId: currentUser?.uid, projectId }),
                    });
                    
                    const result = await refreshResponse.json();
                    if (refreshResponse.ok) {
                        setProject(result.project);
                        toast({
                            title: t("reimportSuccess"),
                            status: "success",
                            duration: 3000,
                        });
                    }
                    setIsReimporting(false);
                } else if (statusData.status === "failed") {
                    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
                    throw new Error(statusData.error || "Scraping failed");
                }
                // Si 'pending' ou 'processing', on continue
            } catch (err) {
                console.error("Polling error:", err);
                if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
                setIsReimporting(false);
                toast({
                    title: t("reimportError"),
                    description: "Erreur lors du suivi de la tâche",
                    status: "error",
                    duration: 3000,
                });
            }
        }, 3000); // Poll toutes les 3s

    } catch (error: any) {
        console.error("Re-import error:", error);
        toast({
            title: t("reimportError"),
            description: error.message,
            status: "error",
            duration: 3000,
        });
        setIsReimporting(false);
    }
  };

  // Afficher le loading si on charge ou si le chargement initial n'est pas terminé
  if (loading || !initialLoadDone) {
    return <LoadingOverlay />;
  }

  if (error) {
    return <Text color="red.500">{error}</Text>;
  }

  if (!project) {
    return <Text>{t('noProjectFound')}</Text>;
  }

  const hasAsinData = project.data && (project.data.main_image || project.data.asin);
  const effectiveAsin = project?.asin || project?.data?.asin;

  // Safe access for feature_bullets with a fallback
  const featureBullets = project.data?.feature_bullets || [];
  const flatBulletListing = Array.isArray(featureBullets) 
    ? featureBullets.join(". ")
    : "";

  return (
    <>
      <Flex p="0 10px" alignItems="flex-start">
        {/* Hidden Input for Upload */}
        <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleImageUpload} 
            style={{ display: 'none' }} 
            multiple 
            accept="image/*" 
            
        />

        {/* Sidebar Image Menu */}
        <ProjectSidebarImages 
          images={buildProductImagesList(project)}
          deletableUrls={new Set<string>(project.images || project.data?.images || [])}
          selectedImageUrl={(() => {
            const list = buildProductImagesList(project);
            return list[currentImageIndex] ?? null;
          })()}
          onSelectImage={(url: string) => {
            const list = buildProductImagesList(project);
            const idx = list.indexOf(url);
            if (idx >= 0) setCurrentImageIndex(idx);
          }}
          onAddImage={() => fileInputRef.current?.click()}
          onDeleteImage={handleDeleteImage}
          isUploading={isUploading}
          token={authToken}
          deletingImage={deletingImage}
        />

        {/* Main Content */}
        <Box flex="1" width="0">
        {hasAsinData ? (
          <>
            <Flex justify="space-between" align="center" mb="20px">
            <Box>
                <MarketplaceSelector 
                    currentLanguage={project.marketplace || 'fr'} 
                    isReadOnly={true}
                />
            </Box>
            {effectiveAsin && (
                <Button
                    leftIcon={<FaAmazon />}
                    colorScheme="orange"
                    variant="outline"
                    onClick={handleReimport}
                    isLoading={isReimporting}
                    size="sm"
                    borderRadius="12px"
                >
                    {t('reimportAmazon')}
                </Button>
            )}
            </Flex>
            <Grid templateColumns="repeat(10, 1fr)" gap={4}>
              <GridItem colSpan={3}>
                <Card p="1rem">
                  <CardBody p="0px 5px">
                    <ProductImageCarousel
                      project={project}
                      currentIndex={currentImageIndex}
                      onIndexChange={setCurrentImageIndex}
                      height="350px"
                      t={t}
                    />
                  </CardBody>
                </Card>
              </GridItem>
              <GridItem colSpan={7}>
                <Card p="1rem">
                  <CardHeader p="12px 5px" mb="12px">
                    <Flex direction="column" gap={2}>
                        <Flex align="center" gap={3}>
                            {isEditingBrand ? (
                                <Flex align="center" gap={2}>
                                    <Input 
                                        size="sm" 
                                        value={editedBrand} 
                                        onChange={(e) => setEditedBrand(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleSaveBrand();
                                          if (e.key === 'Escape') setIsEditingBrand(false);
                                        }}
                                        placeholder={t("brandNamePlaceholder")}
                                        bg="whiteAlpha.200"
                                        color="white"
                                        autoFocus
                                    />
                                    <IconButton
                                        aria-label="Save"
                                        icon={<CheckIcon />}
                                        size="sm"
                                        colorScheme="green"
                                        onClick={handleSaveBrand}
                                    />
                                    <IconButton
                                        aria-label="Cancel"
                                        icon={<CloseIcon />}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setIsEditingBrand(false)}
                                    />
                                </Flex>
                            ) : (
                                <Badge 
                                    colorScheme="purple" 
                                    fontSize="md" 
                                    px={2} 
                                    borderRadius="md"
                                    cursor="pointer"
                                    onClick={() => {
                                        setEditedBrand(project.context?.brand || project.brand || project.data?.brand || "");
                                        setIsEditingBrand(true);
                                    }}
                                    _hover={{ opacity: 0.8 }}
                                >
                                    {project.context?.brand || project.brand || project.data?.brand || t("addBrand")} <Icon as={EditIcon} ml={1} w={3} h={3} />
                                </Badge>
                            )}
                            {effectiveAsin && <Badge>{effectiveAsin}</Badge>}
                        </Flex>
                        {isEditingTitle ? (
                            <Flex align="center" gap={2}>
                                <Input 
                                    size="lg" 
                                    value={editedTitle} 
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveTitle();
                                      if (e.key === 'Escape') setIsEditingTitle(false);
                                    }}
                                    placeholder={t("projectTitlePlaceholder")}
                                    bg="whiteAlpha.200"
                                    color="white"
                                    fontWeight="bold"
                                    autoFocus
                                />
                                <IconButton
                                    aria-label="Save"
                                    icon={<CheckIcon />}
                                    size="sm"
                                    colorScheme="green"
                                    onClick={handleSaveTitle}
                                />
                                <IconButton
                                    aria-label="Cancel"
                                    icon={<CloseIcon />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setIsEditingTitle(false)}
                                />
                            </Flex>
                        ) : (
                            <Flex align="center" gap={2}>
                                <Text 
                                    fontSize="lg" 
                                    color="white" 
                                    fontWeight="bold"
                                    cursor="pointer"
                                    onClick={() => {
                                        setEditedTitle(project.data?.title || project.name || "");
                                        setIsEditingTitle(true);
                                    }}
                                    _hover={{ opacity: 0.8 }}
                                >
                                    {project.data?.title || project.name || t("untitledProject")}
                                </Text>
                                <Icon 
                                    as={EditIcon} 
                                    w={4} h={4} 
                                    color="gray.400" 
                                    cursor="pointer"
                                    onClick={() => {
                                        setEditedTitle(project.data?.title || project.name || "");
                                        setIsEditingTitle(true);
                                    }}
                                    _hover={{ color: "white" }}
                                />
                            </Flex>
                        )}
                    </Flex>
                  </CardHeader>
                  <CardBody p="0px 5px">
                    <Flex direction="column">
                      <Text
                        color="gray.500"
                        fontSize="9.5pt"
                        fontWeight="400"
                        mb="30px"
                      >
                        {featureBullets.map((bullet: any, i: number) => (
                          <p key={i} style={{ color: "#fff", padding: "5px" }}>
                            {bullet}
                          </p>
                        ))}
                      </Text>
                    </Flex>
                  </CardBody>
                </Card>
              </GridItem>
            </Grid>
          </>
        ) : (
          <>
            <Box mb={4}>
                <MarketplaceSelector 
                    currentLanguage={project.marketplace || 'fr'} 
                    isReadOnly={true}
                />
            </Box>

            <Flex justify="flex-end" mb="20px" gap={2}>
              {effectiveAsin && (
                  <Button
                    leftIcon={<FaAmazon />}
                    colorScheme="orange"
                    variant="outline"
                    onClick={handleReimport}
                    isLoading={isReimporting}
                    size="sm"
                    borderRadius="12px"
                  >
                    {t('reimportAmazon')}
                  </Button>
              )}
              <Button
                leftIcon={<FaMagic />}
                colorScheme="teal"
                variant="solid"
                onClick={() => setShowWizard(true)}
                size="sm"
                borderRadius="12px"
              >
                {t('guidedAnalysis')}
              </Button>
            </Flex>

            {/* Display Brand if available in Creator Mode */}
            <Box mb={4}>
                <Flex align="center" gap={3}>
                    {isEditingBrand ? (
                        <Flex align="center" gap={2} maxWidth="300px">
                            <Input 
                                size="sm" 
                                value={editedBrand} 
                                onChange={(e) => setEditedBrand(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveBrand();
                                  if (e.key === 'Escape') setIsEditingBrand(false);
                                }}
                                placeholder={t("brandNamePlaceholder")}
                                bg="whiteAlpha.200"
                                color="white"
                                autoFocus
                            />
                            <IconButton
                                aria-label="Save"
                                icon={<CheckIcon />}
                                size="sm"
                                colorScheme="green"
                                onClick={handleSaveBrand}
                            />
                            <IconButton
                                aria-label="Cancel"
                                icon={<CloseIcon />}
                                size="sm"
                                variant="ghost"
                                onClick={() => setIsEditingBrand(false)}
                            />
                        </Flex>
                    ) : (
                        <Badge 
                            colorScheme="purple" 
                            fontSize="lg" 
                            px={3} 
                            py={1} 
                            borderRadius="md"
                            cursor="pointer"
                            onClick={() => {
                                setEditedBrand(project.context?.brand || project.brand || project.data?.brand || "");
                                setIsEditingBrand(true);
                            }}
                            _hover={{ opacity: 0.8 }}
                        >
                            {project.context?.brand || project.brand || project.data?.brand || t("addBrand")} <Icon as={EditIcon} ml={1} w={3} h={3} />
                        </Badge>
                    )}

                    {isEditingTitle ? (
                        <Flex align="center" gap={2}>
                            <Input 
                                size="lg" 
                                value={editedTitle} 
                                onChange={(e) => setEditedTitle(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveTitle();
                                  if (e.key === 'Escape') setIsEditingTitle(false);
                                }}
                                placeholder={t("projectTitlePlaceholder")}
                                bg="whiteAlpha.200"
                                color="white"
                                fontWeight="bold"
                                autoFocus
                            />
                            <IconButton
                                aria-label="Save"
                                icon={<CheckIcon />}
                                size="sm"
                                colorScheme="green"
                                onClick={handleSaveTitle}
                            />
                            <IconButton
                                aria-label="Cancel"
                                icon={<CloseIcon />}
                                size="sm"
                                variant="ghost"
                                onClick={() => setIsEditingTitle(false)}
                            />
                        </Flex>
                    ) : (
                        <Flex align="center" gap={2}>
                            <Text 
                                fontSize="2xl" 
                                color="white" 
                                fontWeight="bold"
                                    cursor="pointer"
                                    onClick={() => {
                                        setEditedTitle(project.data?.title || project.name || "");
                                        setIsEditingTitle(true);
                                    }}
                                    _hover={{ opacity: 0.8 }}
                                >
                                    {project.data?.title || project.name || t("untitledProject")}
                                </Text>
                                <Icon 
                                    as={EditIcon} 
                                    w={4} h={4} 
                                    color="gray.400" 
                                    cursor="pointer"
                                    onClick={() => {
                                        setEditedTitle(project.data?.title || project.name || "");
                                        setIsEditingTitle(true);
                                    }}
                                    _hover={{ color: "white" }}
                                />
                        </Flex>
                    )}
                </Flex>
            </Box>

            {/* Afficher le wizard si:
                - showWizard est true (clic sur le bouton)
                - OU (pas de contexte existant ET wizard pas skippé)
                - OU mode Analyse : quiz découverte pour enrichir (marque non demandée, contexte pré-rempli)
            */}
            {(() => {
              const hasBrand = project.context?.brand || project.brand || project.data?.brand;
              const hasTitle = project.context?.objectName || project.name || project.data?.title;
              const hasExistingContext = description || hasBrand || hasTitle;
              const isAnalyzeMode = hasAsinData;
              const showForAnalyze = isAnalyzeMode && !wizardSkipped;
              return showWizard || (!wizardSkipped && !hasExistingContext) || showForAnalyze;
            })() && (
              <DescriptionWizard 
                projectId={projectId}
                mode={hasAsinData ? 'analyze' : 'create'}
                skipBrandStep={hasAsinData}
                onComplete={() => {
                  setWizardSkipped(true);
                  setShowWizard(false);
                  fetchProject(); // Refresh data
                }}
                onSkip={() => {
                  setWizardSkipped(true);
                  setShowWizard(false);
                  fetchProject(); // Refresh data (in case partial data was saved)
                }}
              />
            )}

            <ProductBriefCreator projectId={projectId} />
          </>
        )}

        <KeywordsCreator
          projectId={projectId}
          description={description || flatBulletListing}
        />
        <BulletCreator
          projectId={projectId}
          description={description || flatBulletListing}
          productBrief={productBrief}
          onAnalyzeBullet={handleAnalyzeBullet}
          projectData={project}
          onChatOpen={onChatOpen}
          onRoastComplete={fetchProject}
        />
        </Box>
      </Flex>
      
      <ChatWithAbu 
        isOpen={isChatOpen} 
        onClose={onChatClose} 
        projectId={projectId} 
        currentUser={currentUser} 
        setProject={setProject}
        pendingMessage={chatPendingMessage}
        onMessageConsumed={() => setChatPendingMessage(null)}
      />
    </>
  );
};

export default ViewProject;